import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAudioRoom } from '../../Context/AudioRoomContext';
import API from '../../Network/API';
import { toast } from 'react-toastify';
import './AudioRoom.css';
import './AudioRoomSuperUI.css';

const AudioRoom = () => {
    const { roomId: joinRoomId } = useParams();
    const navigate = useNavigate();
    // ─── Page-specific state (not persisted) ───
    const [liveRooms, setLiveRooms] = useState([]);
    const [scheduledRooms, setScheduledRooms] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [roomTitle, setRoomTitle] = useState('');
    const [recordRoom, setRecordRoom] = useState(true);
    const [notifyContacts, setNotifyContacts] = useState(true); // push to contacts — default ON
    // Legacy placeholders
    const [roomDescription, setRoomDescription] = useState('');
    const [roomCategory, setRoomCategory] = useState('General');
    const [sortBy, setSortBy] = useState('listeners');
    const [searchQuery, setSearchQuery] = useState('');
    const [managingParticipant, setManagingParticipant] = useState(null); // { id, role, user_id, user }
    const [isScheduleMode, setIsScheduleMode] = useState(false);
    const [scheduledDate, setScheduledDate] = useState('');
    const [startingScheduledId, setStartingScheduledId] = useState(null);

    // Chat state
    const [showChat, setShowChat] = useState(false);
    const [activePanelTab, setActivePanelTab] = useState('chat');
    const [chatInput, setChatInput] = useState('');
    const [isSendingChat, setIsSendingChat] = useState(false);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [activeFloatingChats, setActiveFloatingChats] = useState([]);
    const chatEndRef = useRef(null);
    const prevChatLenRef = useRef(0);
    // TikTok-live style feed: chat messages and reactions share one overlay;
    // the last 5 entries stay visible until newer ones push them out.
    const pushFeedEntry = useCallback((entry) => {
        setActiveFloatingChats(prev => {
            const next = [...prev, entry];
            while (next.length > 5) next.shift();
            return next;
        });
    }, []);
    const [roomDuration, setRoomDuration] = useState('00:00');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editTitleValue, setEditTitleValue] = useState("");

    // Ping friends state
    const [showPingModal, setShowPingModal] = useState(false);
    const [pingFriends, setPingFriends] = useState([]);
    const [isLoadingPing, setIsLoadingPing] = useState(false);
    const [pingStatus, setPingStatus] = useState({});
    const [pingSearch, setPingSearch] = useState('');

    // DJ Mode state
    const [showPlaySoundModal, setShowPlaySoundModal] = useState(false);
    const [activeDJTrack, setActiveDJTrack] = useState(null);
    const audioContextRef = useRef(null);
    const audioElementRef = useRef(null);
    const fileInputRef = useRef(null);

    // Open Mic join state (X-Spaces style modal)
    const [openMicJoinRoomId, setOpenMicJoinRoomId] = useState(null);

    // Audio Devices state
    const [audioDevices, setAudioDevices] = useState([]);
    const [activeDeviceId, setActiveDeviceId] = useState('');
    const [showDeviceSettingsModal, setShowDeviceSettingsModal] = useState(false);

    useEffect(() => {
        if (showDeviceSettingsModal) {
            const fetchAudioDevices = async () => {
                try {
                    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                        return;
                    }
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const inputs = devices.filter(device => device.kind === 'audioinput');
                    setAudioDevices(inputs);
                } catch (e) {
                    console.error('Failed to get audio devices:', e);
                }
            };
            fetchAudioDevices();
            try {
                if(livekitRoom.current && livekitRoom.current.options && livekitRoom.current.options.audioCaptureDefaults) {
                    setActiveDeviceId(livekitRoom.current.options.audioCaptureDefaults.deviceId || '');
                }
            } catch(e) {}
        }
    }, [showDeviceSettingsModal]);

    const handleDeviceSelect = async (deviceId) => {
        if (!livekitRoom.current) return;
        try {
            await livekitRoom.current.switchActiveDevice('audioinput', deviceId);
            setActiveDeviceId(deviceId);
            toast.success("Microphone swapped smoothly!", { position: "bottom-center", theme: "dark", autoClose: 2000, hideProgressBar: true, style: { fontSize: '14px', borderRadius: '24px', textAlign: 'center', padding: '10px 20px', minHeight: 'auto', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
            setShowDeviceSettingsModal(false);
        } catch (error) {
            console.error("Failed to switch explicitly", error);
            toast.error("Could not switch device.");
        }
    };

    const openPingModal = async () => {
        setShowPingModal(true);
        setPingSearch('');
        setIsLoadingPing(true);
        try {
            const res = await API.get('/friend_list_web');
            if (res.data?.success) {
                const rawUsers = res.data.data?.users || [];
                const uniqueUsers = [];
                const seen = new Set();
                rawUsers.forEach(f => {
                    const uId = f.user_id || f.user?.id || f.friend_id || f.id;
                    if (!seen.has(uId)) {
                        seen.add(uId);
                        uniqueUsers.push(f);
                    }
                });
                setPingFriends(uniqueUsers);
            }
        } catch (e) {
            console.error('Fetch friends failed', e);
        }
        setIsLoadingPing(false);
    };

    const pingFriend = async (friendObj) => {
        if (!activeRoom) return;
        const targetId = friendObj?.user_id || friendObj?.user?.id || friendObj?.friend_id || friendObj?.id;
        if (!targetId) return;

        setPingStatus(prev => ({ ...prev, [targetId]: 'sending' }));
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/ping`, { target_user_id: targetId });
            setPingStatus(prev => ({ ...prev, [targetId]: 'sent' }));
            setTimeout(() => {
                setPingStatus(prev => ({ ...prev, [targetId]: null }));
            }, 3000);
        } catch (e) {
            console.error('Ping friend failed', e);
            setPingStatus(prev => ({ ...prev, [targetId]: 'error' }));
        }
    };

    // ─── Global room state from Context ───
    const {
        activeRoom,
        setActiveRoom,
        currentRole,
        isConnected,
        isMuted,
        activeSpeakers,
        unmutedParticipants,
        handRaised,
        setHandRaised,
        isSpeaker,
        canManage,
        handleCreateRoom,
        handleJoinRoom,
        handleLeaveRoom,
        handleEndRoom,
        toggleMute,
        requestToSpeak,
        lowerHand,
        refreshRoomDetails,
        reactions,
        sendReaction,
        isRecording,
        recordingStartedBy,
        isReconnecting,
        connectionQualities,
        isBrowserOffline,
        isRoomLocked,
        isMuteLocked,
        muteLockMessage,
        muteAll,
        toggleLock,
        startRecording,
        stopRecording,
        stepDown,
        currentUser,
        inviteToSpeak,
        acceptSpeakInvite,
        rejectSpeakInvite,
        revokeCoHost,
        broadcastRoleChange,
        speakerInvite,
        toggleSpeakerHand,
        speakerHandRaisedIds,
        chatMessages,
        setChatMessages,
        isLiveKitConnecting,
        livekitConnectError,
        retryLiveKitConnect,
        editRoomTitle,
        livekitRoom,
        toggleOpenMic,
    } = useAudioRoom();

    // ─── Fetch live rooms ───
    const fetchLiveRooms = useCallback(async () => {
        try {
            let url = '/v1/audio-rooms';
            const params = [];
            if (sortBy) params.push(`sort=${sortBy}`);
            if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
            if (params.length) url += '?' + params.join('&');
            const res = await API.get(url);
            if (res.data?.success) {
                // Defensive guard: never show rooms past the 3h cap, even in the
                // brief window before the backend cleanup cron ends them.
                const MAX_DURATION_MS = 3 * 60 * 60 * 1000;
                const rooms = (res.data.data || []).filter(room => {
                    if (!room.started_at) return true;
                    return (Date.now() - new Date(room.started_at).getTime()) < MAX_DURATION_MS;
                });
                setLiveRooms(rooms);
            }
        } catch (e) {
            console.error('Failed to fetch audio rooms:', e);
        } finally {
            setIsLoading(false);
        }
    }, [sortBy, searchQuery]);

    const fetchScheduledRooms = useCallback(async () => {
        try {
            const res = await API.get('/v1/audio-rooms?status=scheduled');
            if (res.data?.success) {
                setScheduledRooms(res.data.data || []);
            }
        } catch (e) {
            console.error('Failed to fetch scheduled rooms:', e);
        }
    }, []);

    const toggleRSVP = async (roomId) => {
        try {
            const res = await API.post(`/v1/audio-rooms/${roomId}/remind`);
            if (res.data?.success) {
                setScheduledRooms(prev => prev.map(room => {
                    if (room.id === roomId) {
                        return {
                            ...room,
                            is_reminded: res.data.data.is_reminded,
                            reminders_count: res.data.data.reminders_count
                        };
                    }
                    return room;
                }));
            }
        } catch (e) {
            console.error('Failed to toggle RSVP:', e);
        }
    };

    // ─── DJ Mode ───
    const stopDJMode = () => {
        if (activeDJTrack && livekitRoom.current) {
            if (activeDJTrack.stop) activeDJTrack.stop();
            try {
                livekitRoom.current.localParticipant.unpublishTrack(activeDJTrack);
            } catch (e) {}
        }
        setActiveDJTrack(null);

        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.currentTime = 0;
            audioElementRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const startBrowserTabDJ = async () => {
        stopDJMode();
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
            const audioTrack = stream.getAudioTracks()[0];
            if (!audioTrack) {
                toast.error("You must check 'Share tab audio' in the prompt.");
                stream.getTracks().forEach(t => t.stop());
                return;
            }
            stream.getVideoTracks().forEach(t => t.stop());

            if (livekitRoom.current) {
                await livekitRoom.current.localParticipant.publishTrack(audioTrack, {
                    name: "dj_audio_track"
                });
                setActiveDJTrack(audioTrack);
                toast.success("Tab audio is now live!");
            }
            
            audioTrack.onended = () => {
                stopDJMode();
            };
            setShowPlaySoundModal(false);
        } catch (err) {
            console.error("Screen share audio error:", err);
        }
    };

    const startLocalFileDJ = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        stopDJMode();
        
        try {
            const URL = window.URL || window.webkitURL;
            const fileUrl = URL.createObjectURL(file);
            
            const audio = new Audio(fileUrl);
            audioElementRef.current = audio;
            
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioContextClass();
            audioContextRef.current = audioCtx;
            
            const source = audioCtx.createMediaElementSource(audio);
            const destination = audioCtx.createMediaStreamDestination();
            
            source.connect(destination);
            source.connect(audioCtx.destination);
            
            const audioTrack = destination.stream.getAudioTracks()[0];
            if (audioTrack && livekitRoom.current) {
                livekitRoom.current.localParticipant.publishTrack(audioTrack, {
                    name: "dj_audio_track"
                }).then(() => {
                    setActiveDJTrack(audioTrack);
                    toast.success("Playing local file into the room!");
                }).catch(err => {
                    console.error("LiveKit publish err:", err);
                });
            }
            
            audio.play();
            audio.onended = () => {
                stopDJMode();
            };
            setShowPlaySoundModal(false);
        } catch (err) {
            console.error("Local audio error:", err);
            toast.error("Failed to start local audio");
        }
    };

    const renderDJModal = () => {
        if (!showPlaySoundModal) return null;
        return (
            <div className="modal ar-ping-modal-overlay" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 1050 }} onClick={() => setShowPlaySoundModal(false)}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-content" style={{ borderRadius: '15px', border: '1px solid #e9ecef', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', background: '#fff' }}>
                        <div className="modal-header border-bottom-0 pb-0">
                            <h5 className="modal-title" style={{ fontWeight: '600', color: '#111827' }}>Play Sound</h5>
                            <button type="button" className="btn-close" onClick={() => setShowPlaySoundModal(false)}></button>
                        </div>
                            <div className="modal-body pt-4 pb-4">
                                <div className="d-grid gap-3">
                                    <button 
                                        onClick={startBrowserTabDJ} 
                                        className="btn btn-light text-start p-3 d-flex align-items-center ar-dj-btn" 
                                        style={{ border: '1px solid #dee2e6', borderRadius: '12px', background: '#fff', transition: 'all 0.2s' }}
                                    >
                                        <div className="rounded p-2 me-3" style={{ background: '#f3f4f6', color: '#4b5563' }}>
                                            <i className="fa fa-window-maximize fa-lg"></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>Share Browser Tab</div>
                                            <div style={{ fontSize: '13px', color: '#6b7280' }}>Best for YouTube or Spotify</div>
                                        </div>
                                    </button>
                                    
                                    <button 
                                        onClick={() => fileInputRef.current?.click()} 
                                        className="btn btn-light text-start p-3 d-flex align-items-center ar-dj-btn" 
                                        style={{ border: '1px solid #dee2e6', borderRadius: '12px', background: '#fff', transition: 'all 0.2s' }}
                                    >
                                        <div className="rounded p-2 me-3" style={{ background: '#f3f4f6', color: '#4b5563' }}>
                                            <i className="fa fa-file-audio fa-lg"></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>Play Local File</div>
                                            <div style={{ fontSize: '13px', color: '#6b7280' }}>Broadcast an MP3 or WAV file</div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        );
    };

    // ─── Deep link: auto-join room ───
    useEffect(() => {
        if (joinRoomId && !activeRoom && !isJoining) {
            const autoJoin = async () => {
                setIsJoining(true);
                try {
                    await handleJoinRoom(parseInt(joinRoomId));
                    navigate('/audio-rooms', { replace: true });
                } catch (e) {
                    console.error('Failed to join room via link:', e);
                    toast.error('Room not found or has ended.', { theme: "dark", position: "bottom-center", style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
                    navigate('/audio-rooms', { replace: true });
                } finally {
                    setIsJoining(false);
                }
            };
            autoJoin();
        }
    }, [joinRoomId]);

    // ─── Deep link: auto-open Create Modal ───
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('action') === 'create') {
            setShowCreateModal(true);
            window.history.replaceState(null, '', '/audio-rooms'); // clean the URL
        }
    }, []);

    // ─── Room Duration Timer ───
    useEffect(() => {
        if (!activeRoom?.started_at) return;
        const tick = () => {
            const start = new Date(activeRoom.started_at).getTime();
            const elapsed = Math.floor((Date.now() - start) / 1000);
            const h = Math.floor(elapsed / 3600);
            const m = Math.floor((elapsed % 3600) / 60);
            const s = elapsed % 60;
            setRoomDuration(
                h > 0
                    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
                    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
            );
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [activeRoom?.started_at]);

    useEffect(() => {
        fetchLiveRooms();
        fetchScheduledRooms();
        const interval = setInterval(() => {
            fetchLiveRooms();
            fetchScheduledRooms();
        }, 15000);
        return () => clearInterval(interval);
    }, [fetchLiveRooms, fetchScheduledRooms]);

    // ─── Real-time: refresh room lists when a friend's room goes live ───
    // Triggered by Realtime.js dispatching 'audio-room-live' CustomEvent
    // via Soketi WebNotification (no page refresh needed).
    useEffect(() => {
        const handleAudioRoomLive = () => {
            fetchLiveRooms();
            fetchScheduledRooms();
        };
        window.addEventListener('audio-room-live', handleAudioRoomLive);
        return () => window.removeEventListener('audio-room-live', handleAudioRoomLive);
    }, [fetchLiveRooms, fetchScheduledRooms]);

    // ─── Create Room Handler ───
    const onCreateRoom = async () => {
        if (isScheduleMode && !scheduledDate) return;
        setIsCreating(true);
        try {
            const scheduledAt = isScheduleMode ? new Date(scheduledDate).toISOString() : null;
            const finalTitle = roomTitle.trim() ? roomTitle.trim() : 'Live Discussion';
            // Utilizing existing backend signature while overriding text input with finalTitle
            const result = await handleCreateRoom(finalTitle, roomDescription, roomCategory, scheduledAt, recordRoom, { notifyContacts });
            if (result) {
                setShowCreateModal(false);
                setRoomTitle('');
                setIsScheduleMode(false);
                setScheduledDate('');
                fetchLiveRooms();
                fetchScheduledRooms();
            }
        } catch (e) {
            toast.error('Failed to create room. Please try again.', { theme: "dark", position: "bottom-center", style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
        } finally {
            setIsCreating(false);
        }
    };

    // ─── Join Room Handler ───
    const onJoinRoom = async (roomId, preferredRole = 'listener') => {
        setIsJoining(true);
        try {
            await handleJoinRoom(roomId, preferredRole);
        } catch (e) {
            toast.error('Failed to join room.', { theme: "dark", position: "bottom-center", style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
        } finally {
            setIsJoining(false);
        }
    };

    // Intercepts join: shows Open Mic modal for open-mic rooms, otherwise joins directly
    const handleJoinClick = (room) => {
        if (room.is_open_mic) {
            setOpenMicJoinRoomId(room.id);
        } else {
            onJoinRoom(room.id);
        }
    };

    // ─── Leave/End Handlers ───
    const onLeaveRoom = async () => {
        await handleLeaveRoom();
        fetchLiveRooms();
    };

    // ─── Start Scheduled Room Handler ───
    const onStartScheduledRoom = async (roomId) => {
        setStartingScheduledId(roomId);
        try {
            const res = await API.post(`/v1/audio-rooms/${roomId}/start`);
            if (res.data?.success) {
                const { room, token, livekit_url } = res.data.data;
                await handleJoinRoom(room.id);
                fetchLiveRooms();
                fetchScheduledRooms();
            }
        } catch (e) {
            console.error('Failed to start scheduled room:', e);
            toast.error('Failed to start room. Please try again.', { theme: "dark", position: "bottom-center", style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
        } finally {
            setStartingScheduledId(null);
        }
    };

    // ─── Chat Methods ───
    const fetchChatMessages = useCallback(async () => {
        if (!activeRoom?.id) return;
        try {
            const res = await API.get(`/v1/audio-rooms/${activeRoom.id}/messages`);
            if (res.data?.success) {
                const msgs = res.data.data?.data || res.data.data || [];
                setChatMessages(msgs.reverse());
            }
        } catch (e) {
            console.error('Failed to fetch chat messages:', e);
        }
    }, [activeRoom?.id]);

    useEffect(() => {
        if (activeRoom?.id && isConnected) {
            fetchChatMessages();
        }
    }, [activeRoom?.id, isConnected, fetchChatMessages]);

    // Auto-scroll chat
    // Track unread chat messages — only count NEW real-time messages, not initial fetch
    const chatInitializedRef = useRef(false);
    useEffect(() => {
        const currentLen = chatMessages.length;
        if (!chatInitializedRef.current) {
            // First time chatMessages is populated (initial fetch) — don't count as unread
            if (currentLen > 0) {
                chatInitializedRef.current = true;
            }
            prevChatLenRef.current = currentLen;
            return;
        }
        if (currentLen > prevChatLenRef.current) {
            const newMsgs = chatMessages.slice(prevChatLenRef.current);
            // Unread badge counts only messages from OTHER users while the panel is closed
            if (!showChat) {
                const othersCount = newMsgs.filter(m => m.user_id !== currentUser?.id).length;
                if (othersCount > 0) {
                    setUnreadChatCount(prev => prev + othersCount);
                }
            }

            // TikTok-live style feed: every message (own included) is appended
            // and stays until newer entries push it out — no disappear timer.
            newMsgs.forEach(msg => {
                pushFeedEntry({ ...msg, type: 'chat', uniqueId: `c-${msg.id ?? Date.now() + Math.random()}` });
            });
        }
        prevChatLenRef.current = currentLen;
    }, [chatMessages, showChat, currentUser?.id, pushFeedEntry]);

    // Reactions land in the live feed too, so their history stays visible
    // alongside chat messages.
    const seenReactionIdsRef = useRef(new Set());
    useEffect(() => {
        reactions.forEach(r => {
            if (seenReactionIdsRef.current.has(r.id)) return;
            seenReactionIdsRef.current.add(r.id);
            const p = activeRoom?.participants?.find(part => part.user_id === r.userId);
            pushFeedEntry({
                type: 'reaction',
                uniqueId: `r-${r.id}`,
                user: p?.user,
                user_id: r.userId,
                emoji: r.emoji,
            });
        });
        // Reactions are pruned from `reactions` after a couple of seconds, so
        // keep the dedupe set from growing without bound.
        if (seenReactionIdsRef.current.size > 500) {
            seenReactionIdsRef.current = new Set(reactions.map(r => r.id));
        }
    }, [reactions, activeRoom, pushFeedEntry]);
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages]);

    const sendChatMessage = async () => {
        if (!chatInput.trim() || !activeRoom?.id) return;
        setIsSendingChat(true);
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/messages`, {
                message: chatInput.trim()
            });
            setChatInput('');
        } catch (e) {
            console.error('Failed to send chat message:', e);
        } finally {
            setIsSendingChat(false);
        }
    };

    const [showEndConfirm, setShowEndConfirm] = useState(false);

    const onEndRoom = async () => {
        setShowEndConfirm(true);
    };

    const confirmEndRoom = async () => {
        setShowEndConfirm(false);
        await handleEndRoom();
        fetchLiveRooms();
    };

    // ─── Helpers ───
    const getSpeakers = (room) => {
        const speakerRoles = ['host', 'co_host', 'speaker'];
        const roleOrder = { host: 0, co_host: 1, speaker: 2 };
        return (room?.participants || [])
            .filter(p => speakerRoles.includes(p.role))
            .sort((a, b) => (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3));
    };

    const getListeners = (room) =>
        (room?.participants || []).filter(p => p.role === 'listener');

    const getAvatarUrl = (user) => {
        let img = user?.image || user?.avatar;
        if (!img) return null;
        
        // Sanitize broken Mac native php artisan serve 8080 links to proper Nginx proxy bounds
        if (img.startsWith('http') && img.includes(':8080/')) {
            const baseUrl = process.env.REACT_APP_API_URL_HTTPS || process.env.REACT_APP_API_URL;
            if (baseUrl) img = img.replace(/^http:\/\/[^\/]+:8080\//, baseUrl.replace('/api', '') + '/');
        }

        if (img.startsWith('http')) return img;
        
        const base = process.env.REACT_APP_API_URL_HTTPS || process.env.REACT_APP_API_URL;
        return `${base?.replace('/api', '')}/storage/avatar/${img}`;
    };

    // ─── RENDER HELPER ───
    const renderPingModal = () => {
        if (!showPingModal) return null;
        // Client-side name filter — same behaviour as the app's Ping Friends
        // sheet, which filters the fetched list as you type.
        const q = pingSearch.trim().toLowerCase();
        const visibleFriends = q
            ? pingFriends.filter(f =>
                (f?.user?.name || f?.name || '').toLowerCase().includes(q))
            : pingFriends;
        return (
            <div className="modal ar-ping-modal-overlay" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 1050 }} onClick={() => setShowPingModal(false)}>
                <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                    <div className="modal-content" style={{ background: '#181b23', color: '#fff', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                        <div className="modal-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <h5 className="modal-title">Ping Friends</h5>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowPingModal(false)}></button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {!isLoadingPing && pingFriends.length > 0 && (
                                <div style={{ position: 'relative', marginBottom: '12px' }}>
                                    <i className="fa fa-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}></i>
                                    <input
                                        type="text"
                                        placeholder="Search friends..."
                                        value={pingSearch}
                                        onChange={(e) => setPingSearch(e.target.value)}
                                        autoFocus
                                        style={{
                                            width: '100%',
                                            background: 'rgba(255,255,255,0.08)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '20px',
                                            color: '#fff',
                                            fontSize: '14px',
                                            padding: '9px 14px 9px 36px',
                                            outline: 'none',
                                        }}
                                    />
                                </div>
                            )}
                            {isLoadingPing ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-light" role="status"></div>
                                    <p className="mt-2 text-muted">Loading friends...</p>
                                </div>
                            ) : pingFriends.length === 0 ? (
                                <div className="text-center py-4 text-muted">You have no active friends available to ping.</div>
                            ) : visibleFriends.length === 0 ? (
                                <div className="text-center py-4 text-muted">No friends match "{pingSearch.trim()}".</div>
                            ) : (
                                <ul className="list-group list-group-flush" style={{ background: 'transparent' }}>
                                    {visibleFriends.map((f, i) => {
                                        const userId = f?.user_id || f?.user?.id || f?.friend_id || f?.id;
                                        const name = f?.user?.name || f?.name || 'Unknown User';
                                        const status = pingStatus[userId];
                                        let finalAvatar = null;
                                        if (f?.user?.image) {
                                            if(f.user.image.startsWith('http')) finalAvatar = f.user.image;
                                            else finalAvatar = `/storage/avatar/${f.user.image}`;
                                        }

                                        const isInRoom = activeRoom?.participants?.some(p => p.user_id === userId);

                                        return (
                                            <li key={userId || i} className="list-group-item d-flex justify-content-between align-items-center" style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '12px 0' }}>
                                                <div className="d-flex align-items-center">
                                                    {finalAvatar ? (
                                                        <img src={finalAvatar} alt={name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', marginRight: 12, opacity: isInRoom ? 0.5 : 1 }} />
                                                    ) : (
                                                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', marginRight: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isInRoom ? 0.5 : 1 }}>
                                                            {name[0].toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span style={{ color: '#fff', fontWeight: 600, opacity: isInRoom ? 0.5 : 1 }}>{name}</span>
                                                </div>
                                                <button 
                                                    className={`btn btn-sm ${isInRoom ? 'btn-secondary' : status === 'sent' ? 'btn-success' : 'btn-primary'}`}
                                                    style={{ borderRadius: '20px', padding: '6px 16px', fontWeight: 600, opacity: isInRoom ? 0.6 : 1 }}
                                                    onClick={() => !isInRoom && pingFriend(f)}
                                                    disabled={status === 'sending' || status === 'sent' || isInRoom}
                                                >
                                                    {isInRoom ? 'Joined' : status === 'sending' ? 'Sending...' : status === 'sent' ? 'Sent' : 'Ping'}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDeviceModal = () => {
        if (!showDeviceSettingsModal) return null;
        return (
            <div className="modal ar-ping-modal-overlay" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 1050 }} onClick={() => setShowDeviceSettingsModal(false)}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-content" style={{ borderRadius: '15px', border: '1px solid #e9ecef', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', background: '#fff' }}>
                        <div className="modal-header border-bottom-0 pb-0">
                            <h5 className="modal-title" style={{ fontWeight: '600', color: '#111827' }}>Audio Settings</h5>
                            <button type="button" className="btn-close" onClick={() => setShowDeviceSettingsModal(false)}></button>
                        </div>
                        <div className="modal-body pt-4 pb-4">
                            <div className="text-muted mb-3" style={{ fontSize: '13px' }}>Select an active microphone to broadcast from. You may need to have your microphone unmuted to see the exact hardware names here.</div>
                            {audioDevices.length === 0 ? (
                                <p className="text-muted text-center" style={{ fontSize: '14px' }}>No microphone found, or permissions denied.</p>
                            ) : (
                                <div className="d-grid gap-2">
                                    {audioDevices.map((device, i) => (
                                        <button 
                                            key={device.deviceId || i}
                                            onClick={() => handleDeviceSelect(device.deviceId)}
                                            className="btn btn-light text-start p-3 d-flex align-items-center ar-dj-btn"
                                            style={{ 
                                                border: activeDeviceId === device.deviceId ? '2px solid #3b82f6' : '1px solid #dee2e6', 
                                                borderRadius: '12px', 
                                                background: activeDeviceId === device.deviceId ? '#eff6ff' : '#fff', 
                                                transition: 'all 0.2s' 
                                            }}
                                        >
                                            <div className="rounded p-2 me-3" style={{ background: '#f3f4f6', color: '#4b5563' }}>
                                                <i className="fa fa-microphone fa-lg"></i>
                                            </div>
                                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {device.label || 'System Default Microphone'}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ─── RENDER: Active Room View ───
    // Video streams render on their own page (/live-stream) — never here.
    if (activeRoom && isConnected && activeRoom.room_type !== 'video') {
        const speakers = getSpeakers(activeRoom);
        const listeners = getListeners(activeRoom);
        const handRaisedListeners = listeners.filter(p => p.hand_raised_at);

        const getReactionForUser = (userId) => {
            const userReactions = reactions.filter(r => r.userId == userId);
            return userReactions.length > 0 ? userReactions[userReactions.length - 1].emoji : null;
        };

        return (
            <div className="audio-room-page super-ui-mode">
                <div className={`super-ui-card ${showChat ? 'panel-open' : ''}`}>
                    
                    {/* CENTER STAGE */}
                    <div className="super-center-stage">
                        <div className="super-top-bar">
                            <div className="super-room-info">
                                <h4 className="super-room-title d-flex align-items-center">
                                    {isEditingTitle ? (
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '450px', gap: '12px' }}>
                                            <input
                                                type="text"
                                                maxLength={60}
                                                value={editTitleValue}
                                                onChange={(e) => setEditTitleValue(e.target.value)}
                                                autoFocus
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'inherit',
                                                    fontFamily: 'inherit',
                                                    fontSize: 'inherit',
                                                    fontWeight: 'inherit',
                                                    outline: 'none',
                                                    borderBottom: '1px dashed rgba(0,0,0,0.2)',
                                                    flex: 1
                                                }}
                                                onBlur={() => {
                                                    let newTitle = editTitleValue.trim();
                                                    if (newTitle !== "" && newTitle !== activeRoom.title) {
                                                        editRoomTitle(newTitle);
                                                    }
                                                    setIsEditingTitle(false);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        e.target.blur();
                                                    } else if (e.key === 'Escape') {
                                                        setEditTitleValue(activeRoom.title);
                                                        setIsEditingTitle(false);
                                                    }
                                                }}
                                            />
                                            <span style={{ 
                                                fontSize: '12px', 
                                                fontWeight: 600,
                                                color: editTitleValue.length >= 60 ? '#E11D48' : '#9CA3AF',
                                                transition: 'color 0.2s',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {editTitleValue.length}/60
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            {activeRoom.title}
                                            {canManage && (
                                                <i 
                                                    className="fa fa-pencil-alt ms-2 text-muted" 
                                                    style={{cursor: 'pointer', fontSize: '0.75em', padding: '4px'}}
                                                    onClick={() => {
                                                        setEditTitleValue(activeRoom.title);
                                                        setIsEditingTitle(true);
                                                    }}
                                                    title="Edit Room Title"
                                                ></i>
                                            )}
                                        </>
                                    )}
                                </h4>
                                <div className="super-room-listeners">
                                    <span className="ar-live-badge"><span className="ar-live-dot"></span> LIVE</span>
                                    {isRoomLocked && (
                                        <span title="Room is locked — no new participants" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', color: '#4B5563', background: 'rgba(75,85,99,0.12)', border: '1px solid rgba(75,85,99,0.3)' }}>
                                            <i className="fa fa-lock"></i> Locked
                                        </span>
                                    )}
                                    {isRecording && (
                                        <div className="ar-recording-banner" style={{ margin: '0', padding: '4px 8px', fontSize: '11px', gap: '5px', borderRadius: '6px' }}>
                                            <div className="ar-rec-dot"></div> Recording
                                        </div>
                                    )}
                                    <span>🎙 {speakers.length} &nbsp; 🎧 {listeners.length}</span>
                                    {activeRoom.started_at && <span>⏱ {roomDuration}</span>}
                                </div>
                            </div>
                            <div className="dropdown super-host-menu">
                                <button className="super-host-menu-btn dropdown-toggle" data-bs-toggle="dropdown">
                                    <i className="fa fa-ellipsis-h"></i>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                    {canManage && (
                                        <>
                                            <li><a className="dropdown-item clickable" onClick={muteAll}><i className={`fa ${isMuteLocked ? 'fa-volume-up' : 'fa-volume-mute'} me-2`}></i> {isMuteLocked ? 'Unmute All' : 'Mute All'}</a></li>
                                            <li><a className="dropdown-item clickable" onClick={isRecording ? stopRecording : startRecording}><i className={`fa ${isRecording ? 'fa-stop-circle text-danger' : 'fa-circle'} me-2`}></i> {isRecording ? 'Stop Recording' : 'Record Room'}</a></li>
                                            <li><a className="dropdown-item clickable" onClick={toggleLock}><i className={`fa ${isRoomLocked ? 'fa-lock' : 'fa-unlock'} me-2`}></i> {isRoomLocked ? 'Unlock Room' : 'Lock Room'}</a></li>
                                            <li>
                                                <a className="dropdown-item clickable d-flex align-items-center justify-content-between" onClick={toggleOpenMic}>
                                                    <span><i className="fa fa-microphone-lines me-2"></i> Open Mic Mode</span>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '34px',
                                                        height: '20px',
                                                        borderRadius: '10px',
                                                        background: activeRoom?.is_open_mic ? '#22c55e' : '#d1d5db',
                                                        position: 'relative',
                                                        transition: 'background 0.2s',
                                                        flexShrink: 0,
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            top: '2px',
                                                            left: activeRoom?.is_open_mic ? '16px' : '2px',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            background: '#fff',
                                                            transition: 'left 0.2s',
                                                        }} />
                                                    </span>
                                                </a>
                                            </li>
                                            <li><hr className="dropdown-divider" /></li>
                                            <li><a className="dropdown-item clickable" onClick={openPingModal}><i className="fa fa-user-plus me-2"></i> Ping Friends</a></li>
                                            {activeDJTrack ? (
                                                <li><a className="dropdown-item text-danger clickable" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); stopDJMode(); toast.info("DJ Audio stopped."); }}><i className="fa fa-stop-circle me-2"></i> Stop Audio</a></li>
                                            ) : (
                                                <li><a className="dropdown-item clickable" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPlaySoundModal(true); }}><i className="fa fa-music me-2"></i> Play Sound</a></li>
                                            )}
                                        </>
                                    )}
                                    {isSpeaker && (
                                        <>
                                            <li><a className="dropdown-item clickable" onClick={() => setShowDeviceSettingsModal(true)}><i className="fa fa-cog me-2"></i> Audio Settings</a></li>
                                            <li><hr className="dropdown-divider" /></li>
                                        </>
                                    )}
                                    {isSpeaker && currentRole !== 'host' && (
                                        <>
                                            <li><a className="dropdown-item clickable" onClick={() => stepDown()}><i className="fa fa-arrow-down me-2 text-warning"></i> Step Down to Audience</a></li>
                                            <li><hr className="dropdown-divider" /></li>
                                        </>
                                    )}
                                    <li><a className="dropdown-item clickable" onClick={() => {
                                        const shareUrl = `${window.location.origin}/audio-rooms/join/${activeRoom.id}`;
                                        navigator.clipboard.writeText(shareUrl).then(() => {
                                            toast.success('Link copied to clipboard', {
                                                position: "bottom-center",
                                                autoClose: 2000,
                                                hideProgressBar: true,
                                                closeOnClick: true,
                                                pauseOnHover: false,
                                                draggable: true,
                                                theme: "dark",
                                                style: { fontSize: '14px', borderRadius: '24px', textAlign: 'center', padding: '10px 20px', minHeight: 'auto', marginBottom: '80px', width: 'auto', display: 'inline-block' }
                                            });
                                        });
                                    }}><i className="fa fa-link me-2"></i> Copy Link</a></li>
                                </ul>
                            </div>
                        </div>

                        {/* Banners */}
                        {(isReconnecting || isBrowserOffline) && (
                            <div className="ar-reconnecting-banner mt-3" style={{borderRadius:'12px'}}>
                                <div className="ar-reconnecting-spinner"></div>
                                <span>{isBrowserOffline ? 'You are offline' : 'Reconnecting...'}</span>
                            </div>
                        )}
                        {speakerInvite && (
                            <div style={{
                                margin: '12px auto', maxWidth: '480px', backgroundColor: '#ffffff', borderRadius: '24px', padding: '12px 16px',
                                display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)',
                            }}>
                                <div style={{
                                    background: 'rgba(139, 92, 246, 0.12)', borderRadius: '50%', width: '36px', height: '36px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8B5CF6'
                                }}>
                                    <i className="fa fa-microphone"></i>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937' }}>Speaker Invite</span>
                                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>
                                        {speakerInvite.fromName} invited you to speak
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={rejectSpeakInvite} style={{ background: 'rgba(156,163,175,0.1)', color: '#1F2937', border: 'none', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                                        Decline
                                    </button>
                                    <button onClick={acceptSpeakInvite} style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 8px rgba(16,185,129,0.3)' }}>
                                        Accept
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Frictionless Speaker Request Banner */}
                        {canManage && handRaisedListeners.length > 0 && (
                            <div style={{
                                margin: '12px auto', maxWidth: '480px', backgroundColor: '#ffffff', borderRadius: '24px', padding: '12px 16px',
                                display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)',
                            }}>
                                <div style={{
                                    background: 'rgba(245, 158, 11, 0.12)', borderRadius: '50%', width: '36px', height: '36px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D97706'
                                }}>
                                    <i className="fa fa-hand-paper"></i>
                                </div>
                                <div style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {handRaisedListeners.length === 1 
                                        ? `${handRaisedListeners[0].user?.name} wants to speak`
                                        : `${handRaisedListeners.length} requests to speak`}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {handRaisedListeners.length === 1 ? (
                                        <>
                                            <button 
                                                onClick={async () => { 
                                                    const targetUserId = handRaisedListeners[0].user_id;
                                                    // Optimistic UI for Web
                                                    setActiveRoom(prev => {
                                                        if (!prev || !prev.participants) return prev;
                                                        return {
                                                            ...prev,
                                                            participants: prev.participants.map(p => 
                                                                p.user_id === targetUserId ? { ...p, hand_raised_at: null } : p
                                                            )
                                                        };
                                                    });
                                                    try {
                                                        await API.post(`/v1/audio-rooms/${activeRoom.id}/reject-speak/${targetUserId}`); 
                                                        refreshRoomDetails(); 
                                                    } catch (e) { console.error('Reject speak failed:', e); }
                                                }}
                                                style={{ background: 'rgba(156,163,175,0.1)', color: '#1F2937', border: 'none', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                                            >Decline</button>
                                            <button 
                                                onClick={async () => {
                                                    const targetUserId = handRaisedListeners[0].user_id;
                                                    // Optimistic UI for Web
                                                    setActiveRoom(prev => {
                                                        if (!prev || !prev.participants) return prev;
                                                        return {
                                                            ...prev,
                                                            participants: prev.participants.map(p => 
                                                                p.user_id === targetUserId ? { ...p, role: 'speaker', hand_raised_at: null } : p
                                                            )
                                                        };
                                                    });
                                                    try {
                                                        await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${targetUserId}`); 
                                                        // Fallback sync, backend will push role_change too
                                                        refreshRoomDetails();
                                                    } catch (e) {
                                                        console.error('Approve speak failed:', e);
                                                        // Show a toast when toast system is available, but prevent crash overlay
                                                        toast.info('Speaker approved', { autoClose: 2000, position: "bottom-center", theme: "dark", style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' } });
                                                    }
                                                }}
                                                style={{ background: '#E11D48', color: '#fff', border: 'none', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 8px rgba(225,29,72,0.3)' }}
                                            >Approve</button>
                                        </>
                                    ) : (
                                        <button 
                                            onClick={() => { setShowChat(true); setActivePanelTab('audience'); }}
                                            style={{ background: '#E11D48', color: '#fff', border: 'none', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                                        >Review All</button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Speaker Grid */}
                        <div className="super-stage-grid">
                            {speakers.map((p) => {
                                const isSpeaking = [...activeSpeakers].some(id => id.startsWith(String(p.user_id) + '_'));
                                const isMicOpen = [...unmutedParticipants].some(id => id.startsWith(String(p.user_id) + '_'));
                                const emojiReaction = getReactionForUser(p.user_id);
                                return (
                                    <div key={p.id} className={`super-speaker-card ${isSpeaking ? 'speaking' : ''}`}
                                         onClick={() => canManage && !p.isHost && p.user_id !== currentUser?.id ? setManagingParticipant(managingParticipant && managingParticipant.user_id === p.user_id ? null : p) : null}>
                                        <div className="super-avatar-wrap">
                                            <div className="super-avatar-ring">
                                                {getAvatarUrl(p.user) ? (
                                                    <img src={getAvatarUrl(p.user)} className="super-avatar" />
                                                ) : (
                                                    <div className="super-avatar ar-avatar-placeholder" style={{fontSize:'32px'}}>{(p.user?.name || '?')[0].toUpperCase()}</div>
                                                )}
                                            </div>
                                            <span className={`super-mic-indicator ${isMicOpen ? 'active' : 'muted'}`}>
                                                <i className={`fa ${isMicOpen ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
                                            </span>
                                            {emojiReaction && <span className="ar-avatar-reaction" key={Date.now()}>{emojiReaction}</span>}
                                        </div>
                                        <span className="super-speaker-name">{p.user?.name || 'Unknown'}</span>
                                        <span className="super-speaker-role">{p.role === 'host' ? 'Host' : p.role === 'co_host' ? 'Co-host' : 'Speaker'}</span>
                                        {p.hand_raised_at && <span className="ar-hand-indicator">✋</span>}
                                        
                                        {/* Context Menu */}
                                        {managingParticipant && managingParticipant.user_id === p.user_id && (
                                            <div className="ar-participant-menu">
                                                <button onClick={(e) => { e.stopPropagation(); API.post(`/v1/audio-rooms/${activeRoom.id}/demote/${p.user_id}`); setManagingParticipant(null); }}><i className="fa fa-arrow-down"></i> Move to listeners</button>
                                                {currentRole === 'host' && p.role !== 'co_host' && <button onClick={(e) => { e.stopPropagation(); API.post(`/v1/audio-rooms/${activeRoom.id}/co-host/${p.user_id}`); setManagingParticipant(null); }}><i className="fa fa-star"></i> Make co-host</button>}
                                                {currentRole === 'host' && p.role === 'co_host' && <button onClick={(e) => { e.stopPropagation(); revokeCoHost(p.user_id); setManagingParticipant(null); }}><i className="fa fa-star-half-alt"></i> Revoke co-host</button>}
                                                <button className="danger" onClick={(e) => { e.stopPropagation(); API.post(`/v1/audio-rooms/${activeRoom.id}/kick/${p.user_id}`); setManagingParticipant(null); }}><i className="fa fa-ban"></i> Remove</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* GLOBAL FLOATING REACTIONS CANVAS */}
                    <div className="super-floating-reaction-canvas" style={{
                        position: 'absolute',
                        bottom: '120px',
                        right: '40px',
                        width: '80px',
                        height: '60%',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        overflow: 'visible'
                    }}>
                        {reactions.map(r => {
                            const p = activeRoom?.participants?.find(part => part.user_id === r.userId);
                            const avatarUrl = p ? getAvatarUrl(p.user) : null;
                            
                            // Generate a pseudo-random stagger using the ID
                            const leftOffset = (Math.floor(r.id * 100) % 40) - 20; 
                            const initialDelay = (Math.floor(r.id * 10) % 3) * 0.1;

                            return (
                                <div key={r.id} style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: `calc(50% + ${leftOffset}px)`,
                                    animation: `floatUp 2s ease-out forwards ${initialDelay}s`,
                                    opacity: 0 // Starts hidden until animation kicks in
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: 'rgba(0,0,0,0.5)',
                                        padding: '4px 8px',
                                        borderRadius: '24px',
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                    }}>
                                        {avatarUrl ? (
                                            <img src={avatarUrl} alt="" style={{width: 24, height: 24, borderRadius: '50%', objectFit: 'cover'}} />
                                        ) : (
                                            <div style={{width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold'}}>
                                                {(p?.user?.name || '?')[0].toUpperCase()}
                                            </div>
                                        )}
                                        <span style={{fontSize: '24px', lineHeight: 1}}>{r.emoji}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* FLOATING LIVE CHAT OVERLAY */}
                    {activeFloatingChats.length > 0 && !showChat && (
                        <div style={{
                            position: 'absolute',
                            bottom: '110px',
                            left: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            pointerEvents: 'none',
                            zIndex: 100,
                            maxWidth: '75%',
                            alignItems: 'flex-start'
                        }}>
                            {activeFloatingChats.map((msg, index) => {
                                const avatarUrl = getAvatarUrl(msg.user);
                                // Older entries fade slightly, TikTok-live style
                                const ageOpacity = Math.max(0.45, 1 - (activeFloatingChats.length - 1 - index) * 0.15);
                                return (
                                <div key={msg.uniqueId} style={{
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    borderRadius: '18px',
                                    padding: '7px 12px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                    opacity: ageOpacity,
                                    animation: 'feedEntryIn 0.25s ease-out',
                                }}>
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="" style={{width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginTop: '2px'}} />
                                    ) : (
                                        <div style={{width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px'}}>
                                            <i className="fa fa-user" style={{fontSize: '10px', color: '#fff'}}></i>
                                        </div>
                                    )}
                                    {msg.type === 'reaction' ? (
                                        <div style={{fontSize: '13px', color: '#fff'}}>
                                            <span style={{fontWeight: 700, color: 'rgba(255,255,255,0.7)'}}>{msg.user?.name || 'Someone'} </span>
                                            <span style={{fontSize: '16px'}}>{msg.emoji}</span>
                                        </div>
                                    ) : (
                                        <div style={{fontSize: '13px', color: '#fff'}}>
                                            <span style={{fontWeight: 700, color: 'rgba(255,255,255,0.7)'}}>{msg.user?.name}: </span>
                                            <span>{msg.message}</span>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {/* DOCK */}
                    <div className="super-floating-dock">
                        <button className={`super-dock-btn ${showChat && activePanelTab === 'chat' ? 'active' : ''}`} onClick={() => { if(showChat && activePanelTab === 'chat') {setShowChat(false);} else {setShowChat(true); setActivePanelTab('chat'); setUnreadChatCount(0);} }} title="Chat">
                            <i className="fa fa-comment"></i>
                            {unreadChatCount > 0 && (!showChat || activePanelTab !== 'chat') && <span className="super-unread-badge">{unreadChatCount}</span>}
                        </button>

                        <button className={`super-dock-btn ${showChat && activePanelTab === 'audience' ? 'active' : ''}`} onClick={() => { if(showChat && activePanelTab === 'audience') {setShowChat(false);} else {setShowChat(true); setActivePanelTab('audience');} }} title="Audience">
                            <i className="fa fa-users"></i>
                        </button>
                        
                        {isSpeaker ? (
                            <button className={`super-dock-btn primary ${isMuted ? 'muted' : ''}`} onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
                                <i className={`fa ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`}></i>
                            </button>
                        ) : (
                            <button className={`super-dock-btn ${handRaised ? 'orange' : ''}`} onClick={handRaised ? lowerHand : requestToSpeak} title={handRaised ? 'Cancel Request' : 'Request Speaker'}>
                                <i className="fa fa-hand-paper"></i>
                            </button>
                        )}

                        <button className="super-dock-btn danger" onClick={currentRole === 'host' ? onEndRoom : onLeaveRoom} title={currentRole === 'host' ? 'End Room' : 'Leave Room'}>
                            <i className={`fa ${currentRole === 'host' ? 'fa-power-off' : 'fa-sign-out-alt'}`}></i>
                        </button>
                    </div>

                    {/* REACTION ENGINE */}
                    <div className="super-reaction-engine">
                        <div className="super-fanout-container">
                            <button className="super-fanout-btn" onClick={() => sendReaction('🎉')}>🎉</button>
                            <button className="super-fanout-btn" onClick={() => sendReaction('🔥')}>🔥</button>
                            <button className="super-fanout-btn" onClick={() => sendReaction('👏')}>👏</button>
                            <button className="super-fanout-btn" onClick={() => sendReaction('😂')}>😂</button>
                        </div>
                        <button className="super-heart-btn" onClick={() => sendReaction('❤️')} title="React Love">❤️</button>
                    </div>

                    {/* RIGHT SIDE PANEL */}
                    {showChat && (
                        <div className="super-side-panel">
                            <div className="super-panel-header">
                                <button className={`super-panel-tab ${activePanelTab === 'chat' ? 'active' : ''}`} onClick={() => setActivePanelTab('chat')}>Chat</button>
                                <button className={`super-panel-tab ${activePanelTab === 'audience' ? 'active' : ''}`} onClick={() => setActivePanelTab('audience')}>Audience ({listeners.length})</button>
                                <button className="btn-close ms-2 mt-2" style={{opacity:0.5}} onClick={() => setShowChat(false)}></button>
                            </div>
                            
                            {activePanelTab === 'chat' && (
                                <>
                                    <div className="super-panel-body" style={{background:'#F9FAFB'}}>
                                        <div className="ar-chat-messages" style={{padding:0}}>
                                            {chatMessages.length === 0 && <div className="text-center text-muted mt-5">No messages yet.</div>}
                                            {chatMessages.map((msg) => (
                                                <div key={msg.id} className={`ar-chat-msg ${msg.user_id === currentUser?.id ? 'self' : ''}`}>
                                                    <div className="ar-chat-msg-header"><span className="ar-chat-author">{msg.user?.name || 'User'}</span></div>
                                                    <div className="ar-chat-msg-body">{msg.message}</div>
                                                </div>
                                            ))}
                                            <div ref={chatEndRef} />
                                        </div>
                                    </div>
                                    <div className="super-chat-input-area">
                                        <input type="text" placeholder="Type a message..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()} />
                                        <button onClick={sendChatMessage} disabled={isSendingChat || !chatInput.trim()}><i className="fa fa-paper-plane"></i></button>
                                    </div>
                                </>
                            )}

                            {activePanelTab === 'audience' && (
                                <div className="super-panel-body">
                                    {/* Requests */}
                                    {canManage && handRaisedListeners.length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="text-muted text-uppercase" style={{fontSize:'12px', fontWeight:600}}>Hands Raised ({handRaisedListeners.length})</h6>
                                            {handRaisedListeners.map(p => (
                                                <div key={p.id} className="super-listener-item">
                                                    {getAvatarUrl(p.user) ? (
                                                        <img src={getAvatarUrl(p.user)} className="super-listener-avatar" style={{width:36, height:36, objectFit:'cover', borderRadius:'50%'}} alt="Avatar" />
                                                    ) : (
                                                        <div className="super-listener-avatar ar-avatar-placeholder small" style={{width:36, height:36}}>{(p.user?.name || '?')[0].toUpperCase()}</div>
                                                    )}
                                                    <div className="super-listener-info"><div className="super-listener-name">{p.user?.name}</div><div className="super-listener-role text-warning">Wants to speak</div></div>
                                                    <div className="super-listener-actions d-flex gap-2">
                                                        <button onClick={async () => {
                                                            try { await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${p.user_id}`); }
                                                            catch (e) { console.error('Audience promote failed:', e); }
                                                        }} className="text-success"><i className="fa fa-check"></i></button>
                                                        <button onClick={async () => {
                                                            try { await API.post(`/v1/audio-rooms/${activeRoom.id}/reject-speak/${p.user_id}`); }
                                                            catch (e) { console.error('Audience reject failed:', e); }
                                                        }} className="text-danger"><i className="fa fa-times"></i></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Listeners */}
                                    <h6 className="text-muted text-uppercase" style={{fontSize:'12px', fontWeight:600}}>Listening ({listeners.length})</h6>
                                    {listeners.map(p => {
                                        const emojiReaction = getReactionForUser(p.user_id);
                                        return (
                                        <div key={p.id} className="super-listener-item" onClick={() => canManage && setManagingParticipant(managingParticipant?.user_id === p.user_id ? null : p)}>
                                            <div style={{position:'relative'}}>
                                                {getAvatarUrl(p.user) ? (
                                                    <img src={getAvatarUrl(p.user)} className="super-listener-avatar" style={{objectFit:'cover', borderRadius:'50%'}} alt="Avatar" />
                                                ) : (
                                                    <div className="super-listener-avatar ar-avatar-placeholder small">{(p.user?.name || '?')[0].toUpperCase()}</div>
                                                )}
                                                {emojiReaction && <span style={{position:'absolute', bottom:-5, right:-5, fontSize:'14px'}}>{emojiReaction}</span>}
                                            </div>
                                            <div className="super-listener-info">
                                                <div className="super-listener-name">{p.user?.name}</div>
                                            </div>
                                            {managingParticipant?.user_id === p.user_id && canManage && (
                                                <div className="super-listener-actions d-flex gap-2">
                                                    <button title="Invite to speak" onClick={(e) => {e.stopPropagation(); inviteToSpeak(p.user_id); setManagingParticipant(null);}}><i className="fa fa-microphone"></i></button>
                                                    <button title="Kick" onClick={(e) => {e.stopPropagation(); API.post(`/v1/audio-rooms/${activeRoom.id}/kick/${p.user_id}`); setManagingParticipant(null);}}><i className="fa fa-ban text-danger"></i></button>
                                                </div>
                                            )}
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* END CONFIRM OVERLAY */}
                {showEndConfirm && (
                    <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.6)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center'}}>
                        <div style={{background:'white', padding:'32px', borderRadius:'24px', textAlign:'center', maxWidth:300}}>
                            <h5 className="mb-3">End Audio Room?</h5>
                            <p className="text-muted mb-4">This will permanently disconnect all participants.</p>
                            <div className="d-flex gap-2 justify-content-center">
                                <button className="btn btn-light rounded-pill px-4" onClick={() => setShowEndConfirm(false)}>Cancel</button>
                                <button className="btn btn-danger rounded-pill px-4" onClick={confirmEndRoom}>End Room</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* PING MODAL */}
                {renderPingModal()}
                {renderDJModal()}
                {renderDeviceModal()}
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="audio/*" onChange={startLocalFileDJ} />
            </div>
        );
    }

    // ─── RENDER: Room List View ───
    return (
        <div className="min-vh-100 bg-white">
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-lg-8 col-xl-7">
                        <div className="audio-room-page">
            {/* Title + Actions */}
            {/* Live streaming entry points are hidden until the mobile app ships
                with video support — the /live-stream page itself stays reachable
                by URL for testing. */}
            <div className="ar-page-header">
                <div>
                    <h3 className="ar-page-title">
                        <i className="fa fa-microphone me-2"></i>
                        Audio Rooms
                    </h3>
                    <p className="text-muted mb-0">Live audio spaces — join or start your own</p>
                </div>
                <button
                    className="btn ar-create-btn"
                    onClick={() => setShowCreateModal(true)}
                >
                    <i className="fa fa-plus me-2"></i>
                    Start Room
                </button>
            </div>

            {/* Frictionless Create Room Modal */}
            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)'
                }} onClick={() => setShowCreateModal(false)}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '18px', width: '92%', maxWidth: '425px',
                        padding: '28px', boxShadow: '0 12px 36px rgba(0,0,0,0.15)'
                    }} onClick={e => e.stopPropagation()}>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <button className="btn btn-link text-dark p-0 text-decoration-none" onClick={() => setShowCreateModal(false)}>Cancel</button>
                            <h5 className="m-0" style={{ fontWeight: 700, fontSize: '18px' }}>Create Audio Room</h5>
                            <button className="btn btn-link p-0 text-decoration-none" style={{ color: '#000', fontWeight: 600 }} onClick={onCreateRoom}>Done</button>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <textarea
                                style={{
                                    width: '100%', border: 'none', outline: 'none', resize: 'none',
                                    fontSize: '28px', color: '#333', fontWeight: 600,
                                    minHeight: '120px', lineHeight: 1.3, paddingBottom: '20px'
                                }}
                                placeholder="What do you want to talk about?"
                                value={roomTitle}
                                onChange={(e) => setRoomTitle(e.target.value)}
                                maxLength={60}
                                autoFocus
                            />
                            <div style={{
                                position: 'absolute', bottom: '0px', right: '5px',
                                fontSize: '13px', fontWeight: 600,
                                color: roomTitle.length >= 60 ? '#000' : '#a0a3a7'
                            }}>
                                {roomTitle.length} / 60
                            </div>
                        </div>


                        <hr style={{ borderColor: '#e4e6eb', margin: '16px 0' }} />

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <div>
                                <div style={{ fontSize: '16px', fontWeight: 600 }}>Record Room</div>
                                <div style={{ fontSize: '13px', color: '#65676b' }}>Save the conversation for replay.</div>
                            </div>
                            <div className="form-check form-switch" style={{ fontSize: '24px', margin: 0 }}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={recordRoom}
                                    onChange={e => setRecordRoom(e.target.checked)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: recordRoom ? '#000' : '#fff',
                                        borderColor: recordRoom ? '#000' : '#d1d5db',
                                        boxShadow: 'none',
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <div>
                                <div style={{ fontSize: '16px', fontWeight: 600 }}>Notify contacts</div>
                                <div style={{ fontSize: '13px', color: '#65676b' }}>Send a notification to your contacts when you go live.</div>
                            </div>
                            <div className="form-check form-switch" style={{ fontSize: '24px', margin: 0 }}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={notifyContacts}
                                    onChange={e => setNotifyContacts(e.target.checked)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: notifyContacts ? '#000' : '#fff',
                                        borderColor: notifyContacts ? '#000' : '#d1d5db',
                                        boxShadow: 'none',
                                    }}
                                />
                            </div>
                        </div>

                        {isScheduleMode && (
                            <input
                                type="datetime-local"
                                className="form-control mb-3"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                                style={{ borderRadius: '12px', border: '1px solid #e4e6eb', padding: '12px' }}
                            />
                        )}

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                style={{
                                    flex: 1, backgroundColor: '#000', color: '#fff',
                                    border: 'none', borderRadius: '12px', padding: '14px',
                                    fontSize: '16px', fontWeight: 700
                                }}
                                onClick={onCreateRoom}
                                disabled={isCreating || (isScheduleMode && !scheduledDate)}
                            >
                                {isCreating ? <span className="spinner-border spinner-border-sm"></span> : (isScheduleMode ? 'Schedule' : 'Start now')}
                            </button>
                            <button
                                style={{
                                    width: '52px', height: '52px', border: `2px solid ${isScheduleMode ? '#000' : '#e4e6eb'}`,
                                    borderRadius: '50%', backgroundColor: 'transparent',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#000',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setIsScheduleMode(!isScheduleMode)}
                            >
                                <i className="fa fa-calendar-alt" style={{ fontSize: '20px' }}></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sort + Search */}
            <div className="ar-filter-bar">
                <div className="ar-filter-controls">
                    <div className="ar-search-wrap">
                        <i className="fa fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search rooms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="ar-search-input"
                        />
                    </div>
                    <select
                        className="ar-sort-select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="listeners">🔥 Most Listeners</option>
                        <option value="trending">📈 Trending</option>
                        <option value="newest">🆕 Newest</option>
                        <option value="oldest">⏳ Oldest</option>
                    </select>
                </div>
            </div>

            {/* Upcoming Scheduled Rooms */}
            {scheduledRooms.length > 0 && (
                <div className="ar-upcoming-section">
                    <h5 className="ar-section-title">
                        <i className="fa fa-calendar me-2"></i>
                        Upcoming Rooms
                    </h5>
                    <div className="ar-rooms-grid">
                        {scheduledRooms.map((room) => {
                            const isHost = room.host_user_id === currentUser?.id;
                            return (
                                <div key={room.id} className="ar-room-card ar-room-card-scheduled">
                                    <div className="ar-room-card-header">
                                        <div className="ar-room-host">
                                            {getAvatarUrl(room.host) ? (
                                                <img
                                                    src={getAvatarUrl(room.host)}
                                                    alt={room.host?.name}
                                                    className="ar-room-host-avatar"
                                                />
                                            ) : (
                                                <div className="ar-room-host-avatar ar-avatar-placeholder">
                                                    {(room.host?.name || '?')[0].toUpperCase()}
                                                </div>
                                            )}
                                            <div className="ar-room-host-info">
                                                <span className="ar-room-title">{room.title}</span>
                                                <span className="ar-room-host-name">
                                                    {room.host?.name || 'Unknown'} is hosting
                                                </span>
                                            </div>
                                        </div>
                                        <span className="ar-scheduled-badge">
                                            <i className="fa fa-calendar me-1"></i>
                                            {new Date(room.scheduled_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                        </span>
                                    </div>
                                    {room.description && (
                                        <p className="ar-room-description">{room.description}</p>
                                    )}
                                    <div className="ar-room-card-footer">
                                        <div className="ar-room-stats">
                                            <span className="ar-stat ar-scheduled-time">
                                                <i className="fa fa-clock"></i>
                                                {new Date(room.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                            {room.category && (
                                                <span className="ar-stat">{room.category}</span>
                                            )}
                                        </div>
                                        {isHost ? (
                                            <button
                                                className="btn btn-sm ar-start-scheduled-btn"
                                                onClick={() => onStartScheduledRoom(room.id)}
                                                disabled={startingScheduledId === room.id}
                                            >
                                                {startingScheduledId === room.id ? (
                                                    <span className="spinner-border spinner-border-sm"></span>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-play me-1"></i> Start Now
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                className={`btn btn-sm ar-rsvp-btn ${room.is_reminded ? 'rsvped' : ''}`}
                                                onClick={() => toggleRSVP(room.id)}
                                                style={{
                                                    borderRadius: '20px',
                                                    padding: '6px 16px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    border: room.is_reminded ? '1px solid #000' : '1px solid #D1D5DB',
                                                    background: room.is_reminded ? '#f5f5f5' : 'transparent',
                                                    color: room.is_reminded ? '#000' : '#6B7280',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <i className={`fa ${room.is_reminded ? 'fa-bell' : 'fa-bell-slash'} me-2`}></i>
                                                {room.is_reminded ? 'Reminded' : 'Remind Me'} 
                                                {room.reminders_count > 0 && ` (${room.reminders_count})`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Live Rooms */}
            {isLoading ? (
                <div className="ar-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : liveRooms.length === 0 ? (
                <div className="ar-empty">
                    <div className="ar-empty-icon">🎙️</div>
                    <h5>No live rooms right now</h5>
                    <p className="text-muted">Be the first to start an audio room!</p>
                    <button
                        className="btn ar-create-btn"
                        onClick={() => setShowCreateModal(true)}
                    >
                        Start a Room
                    </button>
                </div>
            ) : (
                <div className="ar-rooms-grid">
                    {liveRooms.map((room) => {
                        const speakers = getSpeakers(room);
                        const listeners = getListeners(room);

                        return (
                            <div key={room.id} className="ar-room-card">
                                <div className="ar-room-card-header">
                                    <div className="ar-room-host">
                                        {getAvatarUrl(room.host) ? (
                                            <img
                                                src={getAvatarUrl(room.host)}
                                                alt={room.host?.name}
                                                className="ar-room-host-avatar"
                                            />
                                        ) : (
                                            <div className="ar-room-host-avatar ar-avatar-placeholder">
                                                {(room.host?.name || '?')[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="ar-room-host-info">
                                            <span className="ar-room-title">{room.title}</span>
                                            <span className="ar-room-host-name">
                                                {room.host?.name || 'Unknown'} is hosting
                                            </span>
                                        </div>
                                    </div>
                                    <span className="ar-live-badge">
                                        <span className="ar-live-dot"></span> LIVE
                                    </span>
                                </div>

                                {room.description && (
                                    <p className="ar-room-description">{room.description}</p>
                                )}

                                <div className="ar-room-card-footer">
                                    <div className="ar-room-stats">
                                        <span className="ar-stat">
                                            <i className="fa fa-microphone"></i> {speakers.length}
                                        </span>
                                        <span className="ar-stat">
                                            <i className="fa fa-headphones"></i> {listeners.length}
                                        </span>
                                    </div>
                                    <button
                                        className="btn btn-sm ar-join-btn"
                                        onClick={() => handleJoinClick(room)}
                                        disabled={isJoining}
                                    >
                                        {isJoining ? (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        ) : (
                                            <>
                                                <i className="fa fa-headphones me-1"></i> Tune in
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* Speaker avatars */}
                                {speakers.length > 0 && (
                                    <div className="ar-room-speakers-row">
                                        {speakers.slice(0, 5).map((s, i) => (
                                            <div key={s.id} className="ar-room-speaker-avatar" style={{ marginLeft: i > 0 ? '-8px' : 0 }}>
                                                {getAvatarUrl(s.user) ? (
                                                    <img src={getAvatarUrl(s.user)} alt={s.user?.name} />
                                                ) : (
                                                    <div className="ar-avatar-placeholder small">
                                                        {(s.user?.name || '?')[0].toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Ping Friends Modal */}
            {renderPingModal()}
            
            {/* DJ Play Sound Modal */}
            {renderDJModal()}
            {renderDeviceModal()}
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="audio/*" onChange={startLocalFileDJ} />

            {/* ─── Open Mic Join Modal (X-Spaces style) ─── */}
            {openMicJoinRoomId !== null && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.45)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                    }}
                    onClick={() => setOpenMicJoinRoomId(null)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'rgba(255,255,255,0.92)',
                            borderRadius: '20px',
                            padding: '32px 28px 24px',
                            maxWidth: '340px',
                            width: '90%',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                            textAlign: 'center',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                        }}
                    >
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎙️</div>
                        <h5 style={{ fontWeight: '700', fontSize: '18px', color: '#1c1c1e', margin: '0 0 6px' }}>Open Mic Room</h5>
                        <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 24px', lineHeight: '1.5' }}>
                            How would you like to enter?
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    padding: '14px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                                    background: '#1c1c1e', color: '#fff', fontWeight: '600', fontSize: '15px',
                                }}
                                onClick={async () => {
                                    const id = openMicJoinRoomId;
                                    setOpenMicJoinRoomId(null);
                                    await onJoinRoom(id, 'speaker');
                                }}
                            >
                                <span style={{ fontSize: '20px' }}>🎤</span> Join as Speaker
                            </button>
                            <button
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    padding: '14px 20px', borderRadius: '12px', border: '1.5px solid #e5e7eb', cursor: 'pointer',
                                    background: '#fff', color: '#1c1c1e', fontWeight: '600', fontSize: '15px',
                                }}
                                onClick={async () => {
                                    const id = openMicJoinRoomId;
                                    setOpenMicJoinRoomId(null);
                                    await onJoinRoom(id, 'listener');
                                }}
                            >
                                <span style={{ fontSize: '20px' }}>🎧</span> Join as Listener
                            </button>
                        </div>
                        <button
                            style={{ marginTop: '18px', background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', cursor: 'pointer' }}
                            onClick={() => setOpenMicJoinRoomId(null)}
                        >Cancel</button>
                    </div>
                </div>
            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AudioRoom;
